import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * SEO component to dynamically manage document head metadata.
 * Inject titles, description, keywords, OpenGraph, Twitter tags, canonical URLs, and structured JSON-LD schema.
 */
const SEO = ({
  title,
  description,
  keywords,
  ogImage = '/logo.png',
  ogType = 'website',
  canonicalUrl,
  noIndex = false,
  structuredData
}) => {
  const location = useLocation();

  useEffect(() => {
    // 1. Determine Title
    const baseTitle = "The Tiny Times";
    const formattedTitle = title 
      ? `${title} | ${baseTitle}` 
      : `${baseTitle} | Children's Weekly Magazine`;
    
    document.title = formattedTitle;

    // Helper: Set or Update a Meta Tag
    const setMetaTag = (attributeName, attributeValue, contentValue) => {
      if (contentValue === undefined || contentValue === null) return;
      let element = document.querySelector(`meta[${attributeName}="${attributeValue}"]`);
      if (element) {
        element.setAttribute('content', contentValue);
      } else {
        element = document.createElement('meta');
        element.setAttribute(attributeName, attributeValue);
        element.setAttribute('content', contentValue);
        document.head.appendChild(element);
      }
    };

    // Helper: Set or Update a Link Tag
    const setLinkTag = (relValue, hrefValue) => {
      if (!hrefValue) return;
      let element = document.querySelector(`link[rel="${relValue}"]`);
      if (element) {
        element.setAttribute('href', hrefValue);
      } else {
        element = document.createElement('link');
        element.setAttribute('rel', relValue);
        element.setAttribute('href', hrefValue);
        document.head.appendChild(element);
      }
    };

    // 2. Standard Metadata
    const finalDescription = description || "The Tiny Times - Weekly fun stories, puzzles & learning for children ages 4-15.";
    setMetaTag('name', 'description', finalDescription);
    
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    } else {
      // General keywords fallback
      setMetaTag('name', 'keywords', "children magazine, kids puzzles, children stories, kids learning, weekly activity book, tiny times");
    }

    // 3. Robots Crawling Directives
    if (noIndex) {
      setMetaTag('name', 'robots', 'noindex, nofollow');
    } else {
      setMetaTag('name', 'robots', 'index, follow');
    }

    // 4. Resolve absolute paths for canonicals and images
    const currentOrigin = window.location.origin;
    const absoluteImage = ogImage.startsWith('http') 
      ? ogImage 
      : `${currentOrigin}${ogImage}`;
    
    const absoluteUrl = canonicalUrl || `${currentOrigin}${location.pathname}`;

    // 5. OpenGraph Tags (Facebook, WhatsApp, Discord, Slack etc.)
    setMetaTag('property', 'og:title', formattedTitle);
    setMetaTag('property', 'og:description', finalDescription);
    setMetaTag('property', 'og:image', absoluteImage);
    setMetaTag('property', 'og:url', absoluteUrl);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:site_name', baseTitle);

    // 6. Twitter Card Tags
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:title', formattedTitle);
    setMetaTag('name', 'twitter:description', finalDescription);
    setMetaTag('name', 'twitter:image', absoluteImage);

    // 7. Canonical Tag
    setLinkTag('canonical', absoluteUrl);

    // 8. Structured Data (JSON-LD Schema)
    let scriptTag = document.getElementById('jsonld-seo');
    
    const finalSchema = structuredData || {
      "@context": "https://schema.org",
      "@type": "Periodical",
      "name": "The Tiny Times",
      "url": absoluteUrl,
      "logo": `${currentOrigin}/logo.png`,
      "description": finalDescription,
      "inLanguage": "en",
      "publisher": {
        "@type": "Organization",
        "name": "The Tiny Times",
        "logo": {
          "@type": "ImageObject",
          "url": `${currentOrigin}/logo.png`
        }
      }
    };

    if (scriptTag) {
      scriptTag.textContent = JSON.stringify(finalSchema);
    } else {
      scriptTag = document.createElement('script');
      scriptTag.id = 'jsonld-seo';
      scriptTag.type = 'application/ld+json';
      scriptTag.textContent = JSON.stringify(finalSchema);
      document.head.appendChild(scriptTag);
    }

    // Clean up on component unmount to ensure dynamic script tags do not stack
    return () => {
      const activeScript = document.getElementById('jsonld-seo');
      if (activeScript) {
        activeScript.remove();
      }
    };
  }, [title, description, keywords, ogImage, ogType, canonicalUrl, noIndex, structuredData, location.pathname]);

  return null;
};

export default SEO;
